"use client";

// import { useSupabaseClient } from "@/components/SupabaseProvider";
// import { useEffect, useState } from "react";
// import { FolderModel, deserializeFolder } from "../models/folder_model";
// import { TaskModel, deserializeTask } from "../models/task_model";
// import { VideoModel, deserializeVideo } from "../models/video_model";
// import { Database } from "../types/supabase";

// export const useResource = (resourceId: string, polling = true) => {
//   const supabaseClient = useSupabaseClient();
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [folder, setFolder] = useState<FolderModel | null>(null);
//   const [task, setTask] = useState<TaskModel | null>(null);
//   const [videos, setVideos] = useState<VideoModel[]>([]);

//   const getVideosInFolder = async (folderId: string) => {
//     const { data, error } = await supabaseClient
//       .from("videos_v2")
//       .select("*")
//       .eq("folder_id", folderId);
//
//     if (error) throw error;
//     return data.map(deserializeVideo);
//   };

//   const fetchResource = async () => {
//
//     setIsLoading((prev) => prev);
//     // setError(null);
//     try {
//       if (!resourceId) throw new Error("No resource ID provided");
//       const response = await supabaseClient
//         .from("folders")
//         .select("*, task:tasks(*)")
//         .eq("id", resourceId)
//         .single();

//       if (response.error) throw response.error;
//       if (!response.data) throw new Error("Resource not found");

//       if (response.data.task) {
//
//         setTask(deserializeTask(response.data.task));
//       }
//       const folder = deserializeFolder(response.data);
//       const videos = await getVideosInFolder(folder.id);
//       setVideos(videos);
//       setFolder(deserializeFolder(response.data));
//
//     } catch (err: any) {
//       setError(err);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchResource();
//     if (polling) {
//       const interval = setInterval(() => {
//         fetchResource();
//       }, 10000);
//       return () => clearInterval(interval);
//     }
//   }, [resourceId]);

//   return { folder, task, videos, isLoading, error };
// };

import { useSupabaseClient } from "@/components/SupabaseProvider";
import { useQuery } from "@tanstack/react-query";
import type { VideoModel } from "@ziggapp/klap-core/video";
import { useCallback } from "react";
import {
  FOLDER_ID_LENGTH,
  FolderModel,
  deserializeFolder,
} from "../models/folder_model";
import { TaskModel, deserializeTask } from "../models/task_model";
import { klap } from "../utils/klap";

export type FolderResource = FolderModel & {
  type: "folder";
  task: TaskModel | null;
  videos: VideoModel[];
};
export type VideoResource = VideoModel & {
  type: "video";
  task: TaskModel | null;
};

export type Resource = FolderResource | VideoResource;

export const useResource = (resourceId: string) => {
  const supabaseClient = useSupabaseClient();

  const getVideosResources = async (
    resourceId: string
  ): Promise<VideoResource[]> => {
    const { data, error } = await supabaseClient
      .from("videos_v2")
      .select("*, task:tasks(*)")
      .eq("id", resourceId);

    if (error) throw error;
    return data.map((video) => ({
      ...klap.video.fromRow(video),
      type: "video",
      task: video.task ? deserializeTask(video.task) : null,
    }));
  };

  const getFolderResources = async (resourceId: string) => {
    const { data, error } = await supabaseClient
      .from("folders")
      .select("*, task:tasks(*)")
      .eq("id", resourceId);

    if (error) throw error;
    return data.map((folder) => ({
      ...deserializeFolder(folder),
      type: "folder",
      task: folder.task ? deserializeTask(folder.task) : null,
    }));
  };

  const fetchResource = useCallback(
    async (resourceId: string) => {
      const [folderResources, videoResources] = await Promise.all([
        getFolderResources(resourceId),
        getVideosResources(resourceId),
      ]);
      if (folderResources.length > 0) return folderResources[0];
      if (videoResources.length > 0) return videoResources[0];
      throw new Error("Resource not found");
    },
    [resourceId]
  );

  const query = useQuery({
    queryKey: ["resource", resourceId],
    queryFn: () => fetchResource(resourceId),
  });

  return {
    ...query,
    data: query.data,
  };
};

// Just the name of a folder or video, for pages that load the rest of the
// row elsewhere. Keyed under ["resource", id], so a rename's invalidation
// refreshes it too.
export const useResourceName = (resourceId: string) => {
  const supabaseClient = useSupabaseClient();

  return useQuery({
    queryKey: ["resource", resourceId, "name"],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from(resourceId.length === FOLDER_ID_LENGTH ? "folders" : "videos_v2")
        .select("name")
        .eq("id", resourceId)
        .maybeSingle();
      if (error) throw error;
      return (data?.name as string | undefined) ?? null;
    },
  });
};
